import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { IFormFieldConfig, IFormStepConfig, IOperation, LocalizedText, OPERATION_TYPE } from '../models/user-identification';
import { formatDateFilter } from 'src/app/core/utils/utils';
import { isCatalogBackedFormField, isProvinceFormField } from './form-field-options.util';

export type DynamicFormInputType = 'text' | 'email' | 'number';

/** Umbral Figma 8328:5027 — ≤8 opciones → toggle; >8 → select. */
export const DYNAMIC_FORM_TOGGLE_MAX_OPTIONS = 8;

export function dynamicFormFieldKey(field: Pick<IFormFieldConfig, 'id' | 'name'>): string {
	return (field.id || field.name).toLowerCase();
}

export function isDynamicFormPhonePrefixField(field: Pick<IFormFieldConfig, 'id' | 'name'>): boolean {
	return dynamicFormFieldKey(field) === 'phone_prefix';
}

export function isDynamicFormPhoneField(field: Pick<IFormFieldConfig, 'id' | 'name'>): boolean {
	return dynamicFormFieldKey(field) === 'phone';
}

export function isDynamicFormMultiSelectField(field: Pick<IFormFieldConfig, 'id' | 'name'>): boolean {
	return dynamicFormFieldKey(field) === 'multi_select';
}

export function isDynamicFormSingleSelectField(field: Pick<IFormFieldConfig, 'id' | 'name'>): boolean {
	return dynamicFormFieldKey(field) === 'single_select';
}

export function isDynamicFormMatchOcrField(field: Pick<IFormFieldConfig, 'validators'>): boolean {
	return field.validators?.includes('match_ocr') === true;
}

/** Campo con cotejo (`match_ocr` en validators ≡ match_data del contrato admin). */
export function stepHasMatchDataField(step?: IFormStepConfig): boolean {
	return getVisibleDynamicFormFields(step).some(isDynamicFormMatchOcrField);
}

export function hasWorkflowOcrOperation(operations: IOperation[]): boolean {
	return operations.some(
		(op) => op.type === OPERATION_TYPE.OCR || op.type === OPERATION_TYPE.OCR_CANNY,
	);
}

/** Paso nombre + apellidos (Figma 8328:5258): sin subtítulo de cotejo por defecto. */
export function isDynamicFormNameSurnameStep(fields: IFormFieldConfig[]): boolean {
	const keys = new Set(fields.map((field) => dynamicFormFieldKey(field)));
	return keys.has('name') && keys.has('surname');
}

export function resolveDynamicFormMatchHint(
	hasOcrOperation: boolean,
	translate: (key: string) => string,
): string {
	return translate(hasOcrOperation ? 'FORM.MATCH_HINT.OCR' : 'FORM.MATCH_HINT.AUTH');
}

/**
 * Subtítulo de paso: `alert_msg` → `description` (API) → fallback i18n solo si hay cotejo
 * (`match_ocr` en algún campo) y el paso no es nombre+apellidos.
 */
export function resolveDynamicFormStepDescription(
	step: IFormStepConfig | undefined,
	hasOcrOperation: boolean,
	resolveLocalizedText: (value?: LocalizedText | null) => string,
	translate: (key: string) => string,
): string {
	if (!step) {
		return '';
	}

	if (step.alert_msg) {
		const alertMsg = resolveLocalizedText(step.alert_msg);
		if (alertMsg) {
			return alertMsg;
		}
	}

	if (step.description) {
		const description = resolveLocalizedText(step.description);
		if (description) {
			return description;
		}
	}

	const visibleFields = getVisibleDynamicFormFields(step);
	if (!stepHasMatchDataField(step) || isDynamicFormNameSurnameStep(visibleFields)) {
		return '';
	}

	return resolveDynamicFormMatchHint(hasOcrOperation, translate);
}

/** Obligatorio legal: exige respuesta explícita (Sí o No); `false` («No») es válido. */
export function dynamicFormLegalSelectionValidator(): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		const value = control.value;
		if (value === true || value === false) {
			return null;
		}
		return { required: true };
	};
}

/** Obligatorio multi-select: rechaza `null`, vacío o array sin elementos. */
export function dynamicFormNonEmptyArrayValidator(): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		const value = control.value;
		if (value === null || value === undefined || value === '') {
			return { required: true };
		}
		if (Array.isArray(value) && value.length < 1) {
			return { required: true };
		}
		return null;
	};
}

/** Preset `multi_select` o checkbox con opciones implica selección múltiple aunque el API omita `multiple`. */
export function isDynamicFormFieldMultiple(field: Pick<IFormFieldConfig, 'id' | 'name' | 'multiple' | 'type' | 'options'>): boolean {
	if (isDynamicFormMultiSelectField(field)) {
		return true;
	}
	// Checkbox con opciones (Figma 8328:5167 / NIF-824 QA): multi por defecto; single solo si `multiple: false`.
	if (field.type === 'checkbox' && (field.options?.length ?? 0) > 0 && dynamicFormFieldKey(field) !== 'legal') {
		return field.multiple !== false;
	}
	return !!field.multiple;
}

export function usesDynamicFormToggleField(field: IFormFieldConfig): boolean {
	return field.type === 'radio';
}

/** Preset Sí/No (Figma 8328:5066): `type: radio` con dos opciones binarias. */
export function isDynamicFormYesNoField(field: IFormFieldConfig): boolean {
	if (field.type !== 'radio') {
		return false;
	}
	const key = dynamicFormFieldKey(field);
	if (key === 'single_select') {
		return false;
	}
	if (key === 'yes_no') {
		return true;
	}
	return (field.options?.length ?? 0) === 2;
}

export function usesDynamicFormCheckboxGroup(field: IFormFieldConfig): boolean {
	return (
		field.type === 'checkbox' &&
		(field.options?.length ?? 0) > 0 &&
		!usesDynamicFormLegalField(field) &&
		!isDynamicFormMultiSelectField(field)
	);
}

/** Presets bloqueados (`id_document_type`, `country`) y provincia en cascada: siempre select. */
export function isDynamicFormCatalogSelectField(field: Pick<IFormFieldConfig, 'id' | 'name'>): boolean {
	return isCatalogBackedFormField(field) || isProvinceFormField(field);
}

/**
 * En pasos con país + provincia, fuerza `type: select` en provincia aunque el API envíe `text`
 * (legado previo a NIF-520).
 */
export function coerceProvinceSelectField(
	field: IFormFieldConfig,
	hasCountryProvinceCascade: boolean,
): IFormFieldConfig {
	if (!hasCountryProvinceCascade || !isProvinceFormField(field) || field.type === 'select') {
		return field;
	}
	return { ...field, type: 'select' };
}

/** Paso dirección con cascada país → provincia. */
export function stepHasCountryProvinceCascade(fields: IFormFieldConfig[] | undefined): boolean {
	if (!fields?.length) {
		return false;
	}
	const keys = new Set(fields.map((field) => dynamicFormFieldKey(field)));
	return keys.has('country') && keys.has('province');
}

/** Selección única/múltiple genérica con ≤8 opciones → `tec-toggle`. */
export function usesDynamicFormChoiceToggle(field: IFormFieldConfig, optionCount: number): boolean {
	if (usesDynamicFormLegalField(field) || isDynamicFormCatalogSelectField(field)) {
		return false;
	}
	if (field.type === 'radio') {
		return true;
	}
	if (optionCount < 1 || optionCount > DYNAMIC_FORM_TOGGLE_MAX_OPTIONS) {
		return false;
	}
	return field.type === 'select' || isDynamicFormMultiSelectField(field);
}

/** Select desplegable: catálogos preset, provincia en cascada o `type: select` con más de 8 opciones. */
export function usesDynamicFormSelectDropdown(field: IFormFieldConfig, optionCount: number): boolean {
	if (isDynamicFormCatalogSelectField(field)) {
		return field.type === 'select' || isProvinceFormField(field);
	}
	return (field.type === 'select' || isDynamicFormMultiSelectField(field)) && optionCount > DYNAMIC_FORM_TOGGLE_MAX_OPTIONS;
}

export function usesDynamicFormBooleanCheckbox(field: IFormFieldConfig): boolean {
	return field.type === 'checkbox' && !field.options?.length;
}

export function usesDynamicFormLegalField(field: IFormFieldConfig): boolean {
	return field.id === 'legal';
}

/** Presets de fecha en FORM (MatchData OCR espera `YYYY-MM-DD`). */
const DYNAMIC_FORM_DATE_FIELD_KEYS = new Set(['date', 'birth_date', 'birthdate']);

const ISO_DATE_PREFIX = /^(\d{4}-\d{2}-\d{2})/;

export function isDynamicFormDateField(field: IFormFieldConfig): boolean {
	return DYNAMIC_FORM_DATE_FIELD_KEYS.has(dynamicFormFieldKey(field));
}

/** Normaliza valor de datepicker a `YYYY-MM-DD` para `filled-forms`. */
export function serializeDynamicFormDateValue(raw: unknown): string | null {
	if (raw === null || raw === undefined || raw === '') {
		return null;
	}

	if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
		const year = raw.getFullYear();
		const month = String(raw.getMonth() + 1).padStart(2, '0');
		const day = String(raw.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}`;
	}

	const value = String(raw).trim();
	const isoPrefix = value.match(ISO_DATE_PREFIX);
	if (isoPrefix) {
		return isoPrefix[1];
	}

	const formatted = formatDateFilter(value);
	return formatted || null;
}

export function isDynamicFormNumberField(field: IFormFieldConfig): boolean {
	return dynamicFormFieldKey(field) === 'number';
}

export function isDynamicFormEmailField(field: IFormFieldConfig): boolean {
	return dynamicFormFieldKey(field) === 'email' || field.validators?.includes('email') === true;
}

export function dynamicFormInputType(field: IFormFieldConfig): DynamicFormInputType {
	if (isDynamicFormNumberField(field)) {
		return 'number';
	}
	if (dynamicFormFieldKey(field) === 'email') {
		return 'email';
	}
	return 'text';
}

export function isDynamicFormMultiChoiceCheckbox(field: IFormFieldConfig): boolean {
	return (
		field.type === 'checkbox' &&
		(field.options?.length ?? 0) > 0 &&
		!usesDynamicFormLegalField(field) &&
		isDynamicFormFieldMultiple(field)
	);
}

/** Campos con valor array que requieren al menos una opción marcada. */
export function isDynamicFormMultiChoiceField(field: IFormFieldConfig): boolean {
	if (!isDynamicFormFieldMultiple(field) || (field.options?.length ?? 0) < 1) {
		return false;
	}
	return field.type === 'select' || isDynamicFormMultiSelectField(field) || isDynamicFormMultiChoiceCheckbox(field);
}

export function shouldShowDynamicFormFieldLabel(
	field: IFormFieldConfig,
	_stepTitle: string,
	resolveLocalizedText: (value?: LocalizedText | null) => string,
	_options: { optionCount?: number; visibleFieldCount?: number } = {},
): boolean {
	return Boolean(resolveLocalizedText(field.title).trim());
}

export function dynamicFormFieldLabel(
	field: IFormFieldConfig,
	stepTitle: string,
	resolveLocalizedText: (value?: LocalizedText | null) => string,
	options: { optionCount?: number; visibleFieldCount?: number } = {},
): string {
	return shouldShowDynamicFormFieldLabel(field, stepTitle, resolveLocalizedText, options)
		? resolveLocalizedText(field.title)
		: '';
}

export function isDynamicFormFieldRequired(field: IFormFieldConfig): boolean {
	return field.validators?.includes('required') === true || field.type === 'file';
}

/**
 * Ancho en columnas (grid de 12) del preset dirección (Figma 8328:5342).
 * El front fija la distribución por `id` de campo; ignora el `col` del backend.
 */
const ADDRESS_FIELD_COL_SPAN: Record<string, number> = {
	address: 12,
	address_number: 4,
	address_floor: 4,
	address_door: 4,
	zip_code: 6,
	city: 6,
	country: 6,
	province: 6,
};

export function dynamicFormFieldColSpan(field: IFormFieldConfig): number {
	return ADDRESS_FIELD_COL_SPAN[dynamicFormFieldKey(field)] ?? field.col ?? 12;
}

export function dynamicFormFieldGridColClass(field: IFormFieldConfig): string {
	return `dynamic-form__field--col-${dynamicFormFieldColSpan(field)}`;
}

export function usesDynamicFormWideFieldsLayout(fields: IFormFieldConfig[]): boolean {
	return fields.some((field) => {
		if (isDynamicFormPhonePrefixField(field)) {
			return false;
		}
		const span = dynamicFormFieldColSpan(field);
		return span > 0 && span < 12;
	});
}

export function dynamicFormFieldGroupLabel(
	field: IFormFieldConfig,
	stepTitle: string,
	resolveLocalizedText: (value?: LocalizedText | null) => string,
	options: { optionCount?: number; visibleFieldCount?: number } = {},
): string {
	const label = dynamicFormFieldLabel(field, stepTitle, resolveLocalizedText, options);
	if (!label || !isDynamicFormFieldRequired(field)) {
		return label;
	}
	return `${label}*`;
}

export function dynamicFormLegalControlKeys(field: IFormFieldConfig): string[] {
	if (!field.options?.length) {
		return [field.name];
	}
	return field.options.map((option) => dynamicFormLegalControlKey(field, option.value));
}

export function dynamicFormLegalControlKey(field: IFormFieldConfig, optionValue: string): string {
	return field.options?.length ? `${field.name}__${optionValue}` : field.name;
}

export function dynamicFormLegalOptionLabel(
	field: IFormFieldConfig,
	option: { label: LocalizedText },
	resolveLocalizedText: (value?: LocalizedText | null) => string,
): string {
	const label = resolveLocalizedText(option.label);
	// El asterisco obligatorio no va en el rich text (NIF-550 / NIF-824: texto en toggle-accept).
	if (resolveLocalizedText(field.title).trim() || resolveLocalizedText(field.description).trim()) {
		return label;
	}
	if (!isDynamicFormFieldRequired(field)) {
		return label;
	}
	return `${label}*`;
}

/**
 * Texto del `tec-toggle-accept` sin opciones (NIF-824):
 * description → title. No usar form-group para el contenido legal.
 */
export function dynamicFormLegalAcceptContentLabel(
	field: IFormFieldConfig,
	resolveLocalizedText: (value?: LocalizedText | null) => string,
): string {
	const description = resolveLocalizedText(field.description).trim();
	if (description) {
		return description;
	}
	return resolveLocalizedText(field.title).trim();
}

export function dynamicFormFieldPlaceholder(
	field: IFormFieldConfig,
	resolveLocalizedText: (value?: LocalizedText | null) => string,
): string {
	return resolveLocalizedText(field.description);
}

/**
 * En cascada NIF-520 el país debe renderizarse antes que la provincia
 * aunque el API/preset envíe provincia primero.
 */
export function orderCountryBeforeProvince(fields: IFormFieldConfig[]): IFormFieldConfig[] {
	const countryIdx = fields.findIndex((field) => dynamicFormFieldKey(field) === 'country');
	const provinceIdx = fields.findIndex((field) => dynamicFormFieldKey(field) === 'province');
	if (countryIdx < 0 || provinceIdx < 0 || countryIdx < provinceIdx) {
		return fields;
	}

	const ordered = [...fields];
	const [countryField] = ordered.splice(countryIdx, 1);
	ordered.splice(provinceIdx, 0, countryField);
	return ordered;
}

export function getVisibleDynamicFormFields(step?: IFormStepConfig): IFormFieldConfig[] {
	const fields = step?.fields?.filter((field) => !isDynamicFormPhonePrefixField(field)) ?? [];
	return orderCountryBeforeProvince(fields);
}
